import DoneIcon from '@mui/icons-material/Done';
import RuleIcon from '@mui/icons-material/Rule';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { Box, ListItemIcon, ListItemText } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { ResultsMode } from 'interfaces/experimentResults';
import { find, map, omit } from 'lodash';
import React from 'react';
import { JsonParam, useQueryParam } from 'use-query-params';

interface ResultsModeOption {
  display: string;
  icon: React.ElementType;
  value: ResultsMode;
}

const resultsModeOptions: ResultsModeOption[] = [
  {
    display: 'Published',
    icon: VerifiedUserIcon,
    value: ResultsMode.Published,
  },
  {
    display: 'Approved',
    icon: DoneIcon,
    value: ResultsMode.InternallyApproved,
  },
  {
    display: 'Manual',
    icon: RuleIcon,
    value: ResultsMode.Manual,
  },
];

interface Props {
  iconOnly?: boolean;
}

const ResultsModeDropdown: React.FunctionComponent<Props> = ({ iconOnly = false }) => {
  const [resultsMode, setResultsMode] = useQueryParam('resultsMode');
  const [filterParam, setFilterParam] = useQueryParam('filters', JsonParam);
  const selectedResultsMode =
    find(resultsModeOptions, ({ value }) => value === (resultsMode || ResultsMode.Published)) || resultsModeOptions[0];

  const handleResultsModeChange = (event: SelectChangeEvent) => {
    const newResultsMode = event.target.value;
    if (newResultsMode !== ResultsMode.Manual) {
      const nextFilters = omit(filterParam, 'orchestrationId');
      setFilterParam(Object.keys(nextFilters).length > 0 ? nextFilters : null, 'replaceIn');
    }
    if (newResultsMode !== resultsMode) {
      setResultsMode(newResultsMode, 'replaceIn');
    }
  };

  return (
    <Select
      defaultValue={ResultsMode.Published}
      onChange={handleResultsModeChange}
      value={resultsMode}
      variant="standard"
      label="Result Mode"
      disableUnderline
      inputProps={{ 'aria-label': 'Result mode' }}
      renderValue={(selectedValue) => {
        const activeMode =
          find(resultsModeOptions, ({ value }) => value === (selectedValue || ResultsMode.Published)) ||
          resultsModeOptions[0];
        const ActiveModeIcon = activeMode.icon;

        return (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: iconOnly ? 0 : 1 }}>
            <ActiveModeIcon fontSize="small" />
            {!iconOnly && activeMode.display}
          </Box>
        );
      }}
      SelectDisplayProps={{
        style: { display: 'flex', alignItems: 'center' },
        title: iconOnly ? selectedResultsMode.display : undefined,
      }}
      sx={{
        '& .MuiSelect-select': {
          display: 'flex',
          alignItems: 'center',
          pr: iconOnly ? '28px !important' : undefined,
        },
      }}
    >
      {map(resultsModeOptions, (mode) => (
        <MenuItem key={mode.value} value={mode.value} sx={{ paddingX: 2, paddingY: 1, width: 170 }}>
          <ListItemIcon sx={{ minWidth: 30, color: 'inherit' }}>
            <mode.icon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ paddingX: 0.5 }} primary={mode.display} />
        </MenuItem>
      ))}
    </Select>
  );
};

export default ResultsModeDropdown;
